import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  effect,
  inject,
  afterNextRender,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DialogService, DialogState } from '@modules/shared/services/dialog.service';
import { LightboxService, LightboxState } from '@modules/shared/services/lightbox.service';
import { ModalService } from '@modules/shared/services/modal.service';
import { ShareModalService } from '@modules/shared/services/share.service';

import { MediaVideoModel } from '../models/media-video.model';
import { PlayerModel } from '../models/player.model';
import { PlayerService } from '../service/player.service';

const MOBILE_CONTROL_WIDTH = 768;
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export abstract class VideoDefaultsComponent implements AfterViewInit, OnDestroy, OnInit {
  private platformId = inject(PLATFORM_ID);

  @ViewChild('videoParent', { static: false }) videoParent!: ElementRef;
  @Input() autoplay = false;
  @Input() muted = false;
  @Input() media!: MediaVideoModel;
  @Input() showBackButton = false;
  @Output() backButton = new EventEmitter();
  @Input() hideTitle = false;
  @Input() hideBottomTitle = false;
  @Input() loop = false;

  public afterDestroyHook: () => void = () => {};
  public afterInitHook: () => void = () => {};
  private observer!: MutationObserver;
  public destroy$ = new Subject<boolean>();

  public controlBreakpoints = [0, MOBILE_CONTROL_WIDTH];
  private mouseMoveTimeout: any = null;
  public showControls$ = new Subject<boolean>();
  public player: PlayerModel | null = null;

  public smallControls = false;
  public hoverDelay = 4000;

  public onInitHook: () => void = () => {};

  private wasFullscreen = false;
  public readonly shareService = inject(ShareModalService);
  public readonly lightboxService = inject(LightboxService);
  public readonly dialogService = inject(DialogService);
  public readonly modalService = inject(ModalService);
  public readonly cdr = inject(ChangeDetectorRef);
  public readonly playerService = inject(PlayerService);
  public readonly elRef = inject(ElementRef);
  public readonly renderer = inject(Renderer2);

  protected constructor() {
    afterNextRender(() => {
      effect(() => {
        this.checkFullscreenForOverlay(this.modalService.modalState() === 'open');
      });
      this.lightboxService.lightboxState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
        this.checkFullscreenForOverlay(state === LightboxState.OPEN);
      });
      this.dialogService.dialogState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
        this.checkFullscreenForOverlay(state === DialogState.OPEN);
      });
    });
  }

  private checkFullscreenForOverlay(open: boolean) {
    if (!this.player) {
      return;
    }
    if (open) {
      if (this.player.isFullscreen) {
        this.wasFullscreen = true;
        this.player.toggleFullscreen(false);
      }
    } else {
      if (this.wasFullscreen) {
        this.wasFullscreen = false;
        this.player.toggleFullscreen(true);
      }
    }
  }

  showControls() {
    this.showControls$.next(true);
    if (this.mouseMoveTimeout) {
      clearTimeout(this.mouseMoveTimeout);
    }
    this.mouseMoveTimeout = setTimeout(() => {
      this.showControls$.next(false);
    }, this.hoverDelay);
  }

  checkControlWidth(width: any) {
    this.smallControls = width < MOBILE_CONTROL_WIDTH;
    this.cdr.detectChanges();
  }

  openShare() {
    if (!this.player) return;

    this.shareService.openModal({
      shareData: {
        title: this.player.playerData.title,
        content: this.player.playerData.description,
        url: location.href,
      },
    });
  }

  checkAutoPlay() {
    if (this.autoplay && !this.muted) {
      /**
       * to autoplay a video, we have to set it to muted manually.
       * if the video was not intentionally muted, we unmute it again
       */
      setTimeout(() => {
        if (!this.player) return;

        this.player.unmute();
      }, 100);
    }
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.player = this.playerService.createPlayer(this.media, {
      autoplay: this.autoplay,
      muted: this.muted,
      loop: this.loop,
    });
    if (!this.player) return;

    this.player.setFullscreenTarget(this.elRef.nativeElement);
    this.afterInitHook();
    this.player.play();
  }

  ngAfterViewInit() {
    if (!this.player) return;

    const videoParent = this.videoParent.nativeElement;
    /**
     * in safari on ios devices the player gets replaced with the
     * video player component of iOS. this will result in misbehavior,
     * so we need to set the proper instance of the new video player element.
     */
    this.observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const videoEl = this.videoParent.nativeElement.querySelector('video');
          if (videoEl && this.player) {
            this.player.videoEl = videoEl;
            this.checkAutoPlay();
          }
        }
      }
    });
    this.observer.observe(videoParent, { childList: true });
    this.renderer.appendChild(this.videoParent.nativeElement, this.player.videoEl);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.observer.disconnect();
    this.playerService.destroyFlyout();

    if (this.player) {
      this.player.stop();
      this.player.player.dispose();
    }

    if (this.afterDestroyHook) {
      this.afterDestroyHook();
    }
  }
}
